/**
 * Conversation ID generation for @spatius/avatarkit-rtc.
 *
 * Format matches web-sdk exactly so both SDKs' telemetry can be read the same
 * way: UTC timestamp (YYYYMMDDHHmmss) + underscore + 12-character NanoID.
 * Example: 20251027143034_aB3dEf9hIjKl
 *
 * Unlike web-sdk the RTC SDK never receives a conversation id — audio goes up
 * through the RTC provider to the agent, which drives the avatar service on our
 * behalf, so nothing in the downstream animation carries a request id. The id is
 * therefore generated locally, once per playback round, purely to correlate this
 * SDK's own telemetry (events, metrics, trace). It is deliberately NOT presented
 * as the server's conversation id.
 *
 * web-sdk pulls this from the `nanoid` package; reimplemented here (same
 * alphabet, same length) to avoid adding a dependency for twelve characters.
 *
 * @packageDocumentation
 * @internal
 */

/** URL-safe alphabet: A-Z, a-z, 0-9 (62 chars), matching web-sdk. */
const ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const NANOID_LENGTH = 12;

/**
 * Generate a 12-character NanoID.
 *
 * Uses crypto.getRandomValues where available. The modulo over 62 is very
 * slightly biased (256 % 62 !== 0) but this id only has to be unique within a
 * session's telemetry, not unpredictable, so the bias is irrelevant.
 * @internal
 */
function nanoid(): string {
  const bytes = new Uint8Array(NANOID_LENGTH);
  const cryptoObj =
    typeof globalThis !== 'undefined'
      ? (globalThis.crypto as Crypto | undefined)
      : undefined;

  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < NANOID_LENGTH; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let out = '';
  for (let i = 0; i < NANOID_LENGTH; i++) {
    out += ALPHABET[bytes[i] % ALPHABET.length];
  }
  return out;
}

/**
 * Generate a conversation id for one playback round.
 * @returns id shaped `YYYYMMDDHHmmss_<12-char nanoid>`
 * @internal
 */
export function generateConversationId(): string {
  const now = new Date();
  const timestamp =
    String(now.getUTCFullYear()) +
    String(now.getUTCMonth() + 1).padStart(2, '0') +
    String(now.getUTCDate()).padStart(2, '0') +
    String(now.getUTCHours()).padStart(2, '0') +
    String(now.getUTCMinutes()).padStart(2, '0') +
    String(now.getUTCSeconds()).padStart(2, '0');

  return `${timestamp}_${nanoid()}`;
}
